/**
 * 모달 Analytics 추적 시스템
 * 모달 열기/닫기, 중첩, 전환 등 모든 모달 상호작용을 추적
 */

import { ref, reactive, onUnmounted } from 'vue';
import { useAnalytics, EVENT_TYPES, CONTENT_TYPES } from './useAnalytics';

// 전역 모달 상태 관리 (메모리 최적화)
const modalStack = ref([]);
const modalSession = reactive({
  sessionId: Date.now().toString(),
  startTime: Date.now(),
  interactions: [],
  maxInteractions: 100 // 메모리 누수 방지를 위한 최대 저장 수
});

// 모달 타입 정의
export const MODAL_TYPES = {
  // 일반 모달
  ARTWORK_DETAIL: 'artwork_detail',
  ARTIST_PROFILE: 'artist_profile',
  IMAGE_VIEWER: 'image_viewer',
  
  // 인증 관련
  LOGIN: 'login',
  SIGNUP: 'signup',
  
  // 액션 모달
  SHARE: 'share',
  DOWNLOAD: 'download',
  DELETE_CONFIRM: 'delete_confirm',
  
  // 폼 모달
  INQUIRY: 'inquiry',
  CONTACT: 'contact',
  PROFILE_EDIT: 'profile_edit',
  
  // 미디어 모달
  VIDEO_PLAYER: 'video_player',
  GALLERY: 'gallery',
  PDF_VIEWER: 'pdf_viewer',
  
  // 기타
  NOTIFICATION: 'notification',
  HELP: 'help',
  SETTINGS: 'settings'
};

// 모달 카테고리 정의
export const MODAL_CATEGORIES = {
  CONTENT: 'content',        // 콘텐츠 조회
  AUTH: 'auth',             // 인증 관련
  ACTION: 'action',         // 사용자 액션
  FORM: 'form',             // 폼 입력
  MEDIA: 'media',           // 미디어 조회
  SYSTEM: 'system'          // 시스템 알림
};

export function useModalAnalytics() {
  const { trackEvent } = useAnalytics();
  
  // 개발 환경 체크 유틸리티
  const isDev = () => process.env.NODE_ENV === 'development';
  
  /**
   * 모달 열기 추적
   * @param {string} modalType - 모달 타입 (MODAL_TYPES 사용)
   * @param {Object} modalData - 모달 관련 데이터
   * @param {Object} context - 추가 컨텍스트 정보
   */
  const trackModalOpen = (modalType, modalData = {}, context = {}) => {
    const modalId = `${modalType}_${Date.now()}`;
    const timestamp = Date.now();
    
    // 모달 스택에 추가
    modalStack.value.push({
      id: modalId,
      type: modalType,
      openTime: timestamp,
      data: modalData,
      context: context,
      interactions: []
    });
    
    // Analytics 이벤트 추적
    trackEvent('modal_open', {
      modal_type: modalType,
      modal_id: modalId,
      modal_category: getModalCategory(modalType),
      stack_depth: modalStack.value.length,
      parent_modal: modalStack.value.length > 1 ? modalStack.value[modalStack.value.length - 2].type : null,
      trigger_source: context.triggerSource || 'unknown',
      content_id: modalData.contentId || null,
      content_type: modalData.contentType || null,
      page_path: window.location.pathname,
      session_id: modalSession.sessionId
    });
    
    // 세션에 기록 (메모리 관리)
    if (modalSession.interactions.length >= modalSession.maxInteractions) {
      // 오래된 상호작용 제거 (FIFO)
      modalSession.interactions.splice(0, Math.floor(modalSession.maxInteractions / 2));
    }
    modalSession.interactions.push({
      type: 'open',
      modalType,
      modalId,
      timestamp,
      stackDepth: modalStack.value.length
    });
    
    return modalId;
  };
  
  /**
   * 모달 닫기 추적
   * @param {string} modalType - 모달 타입
   * @param {string} closeReason - 닫기 이유 ('user_action', 'escape', 'background_click', 'navigation')
   * @param {Object} result - 모달에서 반환된 결과
   */
  const trackModalClose = (modalType, closeReason = 'user_action', result = {}) => {
    const currentModal = modalStack.value.find(m => m.type === modalType);
    if (!currentModal) {
      if (isDev()) {
        console.warn(`Modal close tracking failed: Modal type '${modalType}' not found in stack`);
      }
      return false;
    }
    
    const timestamp = Date.now();
    const duration = timestamp - currentModal.openTime;
    
    // Analytics 이벤트 추적
    trackEvent('modal_close', {
      modal_type: modalType,
      modal_id: currentModal.id,
      modal_category: getModalCategory(modalType),
      close_reason: closeReason,
      duration_ms: duration,
      duration_seconds: Math.round(duration / 1000),
      stack_depth: modalStack.value.length,
      had_interactions: currentModal.interactions.length > 0,
      interaction_count: currentModal.interactions.length,
      success: result.success !== false,
      result_data: result.data || null,
      session_id: modalSession.sessionId
    });
    
    // 모달 스택에서 제거
    modalStack.value = modalStack.value.filter(m => m.id !== currentModal.id);
    
    // 세션에 기록 (메모리 관리)
    if (modalSession.interactions.length >= modalSession.maxInteractions) {
      modalSession.interactions.splice(0, Math.floor(modalSession.maxInteractions / 2));
    }
    modalSession.interactions.push({
      type: 'close',
      modalType,
      modalId: currentModal.id,
      timestamp,
      duration,
      closeReason,
      stackDepth: modalStack.value.length
    });
  };
  
  /**
   * 모달 내 상호작용 추적
   * @param {string} modalType - 모달 타입
   * @param {string} interactionType - 상호작용 타입 ('click', 'form_submit', 'navigation', 'error')
   * @param {Object} details - 상호작용 세부 정보
   */
  const trackModalInteraction = (modalType, interactionType, details = {}) => {
    const currentModal = modalStack.value.find(m => m.type === modalType);
    if (!currentModal) {
      if (isDev()) {
        console.warn(`Modal interaction tracking failed: Modal type '${modalType}' not found in stack`);
      }
      return false;
    }
    
    const timestamp = Date.now();
    const timeFromOpen = timestamp - currentModal.openTime;
    
    // 모달 내 상호작용 기록
    currentModal.interactions.push({
      type: interactionType,
      timestamp,
      timeFromOpen,
      details
    });
    
    // Analytics 이벤트 추적
    trackEvent('modal_interaction', {
      modal_type: modalType,
      modal_id: currentModal.id,
      modal_category: getModalCategory(modalType),
      interaction_type: interactionType,
      time_from_open: timeFromOpen,
      element_id: details.elementId || null,
      element_type: details.elementType || null,
      action_value: details.value || null,
      session_id: modalSession.sessionId
    });
  };
  
  /**
   * 모달 전환 추적 (모달에서 다른 모달로)
   * @param {string} fromModalType - 출발 모달
   * @param {string} toModalType - 도착 모달
   * @param {Object} context - 전환 컨텍스트
   */
  const trackModalTransition = (fromModalType, toModalType, context = {}) => {
    trackEvent('modal_transition', {
      from_modal: fromModalType,
      to_modal: toModalType,
      from_category: getModalCategory(fromModalType),
      to_category: getModalCategory(toModalType),
      transition_trigger: context.trigger || 'unknown',
      stack_depth: modalStack.value.length,
      session_id: modalSession.sessionId
    });
    
    // 세션에 기록 (메모리 관리)
    if (modalSession.interactions.length >= modalSession.maxInteractions) {
      modalSession.interactions.splice(0, Math.floor(modalSession.maxInteractions / 2));
    }
    modalSession.interactions.push({
      type: 'transition',
      from: fromModalType,
      to: toModalType,
      timestamp: Date.now(),
      trigger: context.trigger
    });
  };
  
  /**
   * 모달 오류 추적
   * @param {string} modalType - 모달 타입
   * @param {string} errorType - 오류 타입
   * @param {string} errorMessage - 오류 메시지
   */
  const trackModalError = (modalType, errorType, errorMessage) => {
    trackEvent('modal_error', {
      modal_type: modalType,
      modal_category: getModalCategory(modalType),
      error_type: errorType,
      error_message: errorMessage,
      session_id: modalSession.sessionId
    });
  };
  
  /**
   * 모달 성능 추적
   * @param {string} modalType - 모달 타입
   * @param {number} loadTime - 로딩 시간 (ms)
   * @param {Object} metrics - 성능 지표
   */
  const trackModalPerformance = (modalType, loadTime, metrics = {}) => {
    trackEvent('modal_performance', {
      modal_type: modalType,
      modal_category: getModalCategory(modalType),
      load_time_ms: loadTime,
      load_time_seconds: Math.round(loadTime / 1000),
      content_size: metrics.contentSize || null,
      image_count: metrics.imageCount || null,
      api_calls: metrics.apiCalls || null,
      session_id: modalSession.sessionId
    });
  };
  
  /**
   * 모달 세션 요약 생성
   */
  const getModalSessionSummary = () => {
    const currentTime = Date.now();
    const sessionDuration = currentTime - modalSession.startTime;
    
    const summary = {
      sessionId: modalSession.sessionId,
      duration: sessionDuration,
      totalInteractions: modalSession.interactions.length,
      modalTypes: [...new Set(modalSession.interactions.map(i => i.modalType || i.to || i.from))].filter(Boolean),
      maxStackDepth: modalSession.interactions.length > 0 ? Math.max(...modalSession.interactions.map(i => i.stackDepth || 0)) : 0,
      openCount: modalSession.interactions.filter(i => i.type === 'open').length,
      closeCount: modalSession.interactions.filter(i => i.type === 'close').length,
      transitionCount: modalSession.interactions.filter(i => i.type === 'transition').length
    };
    
    return summary;
  };
  
  /**
   * 모달 카테고리 결정
   * @param {string} modalType - 모달 타입
   * @returns {string} 모달 카테고리
   */
  const getModalCategory = (modalType) => {
    const categoryMap = {
      [MODAL_TYPES.ARTWORK_DETAIL]: MODAL_CATEGORIES.CONTENT,
      [MODAL_TYPES.ARTIST_PROFILE]: MODAL_CATEGORIES.CONTENT,
      [MODAL_TYPES.IMAGE_VIEWER]: MODAL_CATEGORIES.MEDIA,
      [MODAL_TYPES.LOGIN]: MODAL_CATEGORIES.AUTH,
      [MODAL_TYPES.SIGNUP]: MODAL_CATEGORIES.AUTH,
      [MODAL_TYPES.SHARE]: MODAL_CATEGORIES.ACTION,
      [MODAL_TYPES.DOWNLOAD]: MODAL_CATEGORIES.ACTION,
      [MODAL_TYPES.DELETE_CONFIRM]: MODAL_CATEGORIES.ACTION,
      [MODAL_TYPES.INQUIRY]: MODAL_CATEGORIES.FORM,
      [MODAL_TYPES.CONTACT]: MODAL_CATEGORIES.FORM,
      [MODAL_TYPES.PROFILE_EDIT]: MODAL_CATEGORIES.FORM,
      [MODAL_TYPES.VIDEO_PLAYER]: MODAL_CATEGORIES.MEDIA,
      [MODAL_TYPES.GALLERY]: MODAL_CATEGORIES.MEDIA,
      [MODAL_TYPES.PDF_VIEWER]: MODAL_CATEGORIES.MEDIA,
      [MODAL_TYPES.NOTIFICATION]: MODAL_CATEGORIES.SYSTEM,
      [MODAL_TYPES.HELP]: MODAL_CATEGORIES.SYSTEM,
      [MODAL_TYPES.SETTINGS]: MODAL_CATEGORIES.SYSTEM
    };
    
    return categoryMap[modalType] || MODAL_CATEGORIES.SYSTEM;
  };
  
  /**
   * 페이지 언로드 시 열린 모달들 정리 (안전한 방식)
   */
  const cleanup = () => {
    // 무한 루프 방지를 위해 스택 복사 후 정리
    const modalsToClose = [...modalStack.value];
    modalStack.value = []; // 먼저 스택 비우기
    
    modalsToClose.forEach(modal => {
      // trackModalClose 대신 직접 이벤트 전송
      try {
        trackEvent('modal_close', {
          modal_type: modal.type,
          modal_id: modal.id,
          close_reason: 'navigation',
          duration_ms: Date.now() - modal.openTime,
          success: false,
          session_id: modalSession.sessionId
        });
      } catch (error) {
        // 조용히 실패 (cleanup 중 에러 방지)
      }
    });
    
    // 세션 요약 추적
    try {
      const summary = getModalSessionSummary();
      if (summary.totalInteractions > 0) {
        trackEvent('modal_session_summary', summary);
      }
    } catch (error) {
      // 조용히 실패
    }
  };
  
  // 컴포넌트 언마운트 시 정리
  onUnmounted(cleanup);
  
  // 페이지 언로드 시 정리
  if (typeof window !== 'undefined') {
    window.addEventListener('beforeunload', cleanup);
  }
  
  return {
    // 기본 추적 함수
    trackModalOpen,
    trackModalClose,
    trackModalInteraction,
    trackModalTransition,
    trackModalError,
    trackModalPerformance,
    
    // 상태 관리
    modalStack: modalStack.value,
    getCurrentModal: () => modalStack.value[modalStack.value.length - 1] || null,
    getModalCount: () => modalStack.value.length,
    isModalOpen: (modalType) => modalStack.value.some(m => m.type === modalType),
    
    // 유틸리티
    getModalSessionSummary,
    cleanup,
    
    // 상수
    MODAL_TYPES,
    MODAL_CATEGORIES
  };
}